import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  FiFileText, FiMonitor, FiDroplet, FiTool, 
  FiCoffee, FiSearch, FiCheckCircle, FiArrowRight 
} from 'react-icons/fi';

const Products = () => {
  const productCategories = [
    {
      icon: FiFileText,
      title: 'Office Stationery & Supplies',
      description: 'Complete range of office essentials for seamless daily operations and productivity',
      products: [
        'Pens, Pencils & Markers',
        'Paper & Notebooks',
        'Printer Cartridges & Toners',
        'Folders & File Organizers',
        'Staplers & Hole Punchers',
        'Binders & Document Sleeves',
        'Envelopes & Mailing Supplies',
        'Labels & Adhesives',
        'Desk Accessories',
        'Presentation Materials',
      ],
      brands: ['HP', 'Canon', 'Epson', 'Samsung'],
    },
    {
      icon: FiMonitor,
      title: 'ICT Equipment & Accessories',
      description: 'Modern technology solutions to empower your business with the latest equipment',
      products: [
        'Desktop Computers',
        'Laptops & Notebooks',
        'Printers & Scanners',
        'Networking Equipment',
        'Cables & Adapters',
        'External Storage Devices',
        'Computer Peripherals',
        'UPS & Power Solutions',
        'Software Licenses',
        'IT Accessories',
      ],
      brands: ['Dell', 'HP', 'Lenovo', 'Cisco', 'TP-Link'],
    },
    {
      icon: FiDroplet,
      title: 'Cleaning & Janitorial Supplies',
      description: 'Professional-grade cleaning products for maintaining pristine, healthy workspaces',
      products: [
        'Detergents & Soaps',
        'Disinfectants & Sanitizers',
        'Brooms & Brushes',
        'Mops & Buckets',
        'Trash Bags & Bins',
        'Cleaning Cloths & Towels',
        'Floor Care Products',
        'Air Fresheners',
        'Safety Equipment',
        'Janitorial Carts',
      ],
      brands: ['Dettol', 'Vim', 'Domestos', 'Clorox'],
    },
    {
      icon: FiTool,
      title: 'Building & Construction Materials',
      description: 'Quality construction materials and hardware for all your building projects',
      products: [
        'Paint & Painting Supplies',
        'Nails, Screws & Fasteners',
        'Hand Tools & Power Tools',
        'Plumbing Fixtures',
        'Electrical Fittings',
        'Hardware & Locks',
        'Cement & Concrete',
        'Lumber & Wood Products',
        'Safety Gear',
        'Measuring Tools',
      ],
      brands: ['Crown Paints', 'Makita', 'Bosch', 'Stanley'],
    },
    {
      icon: FiCoffee,
      title: 'Hospitality Supplies',
      description: 'Premium equipment and supplies for hotels, restaurants, and catering services',
      products: [
        'Kitchen Equipment',
        'Cookware & Utensils',
        'Food Service Items',
        'Consumables & Disposables',
        'Serving Ware & Trays',
        'Food Storage Containers',
        'Small Appliances',
        'Table Linens',
        'Guest Amenities',
        'Bar Supplies',
      ],
      brands: ['Tefal', 'Kenwood', 'Pyrex', 'Prestige'],
    },
    {
      icon: FiSearch,
      title: 'Custom Procurement Solutions',
      description: 'Specialized sourcing services for unique requirements and project-specific needs',
      products: [
        'Any Special Request Items',
        'Bulk Order Management',
        'Project-Specific Materials',
        'Seasonal Supplies',
        'Specialized Equipment',
        'Import Procurement',
        'Emergency Orders',
        'Tender-Based Supply',
        'Long-Term Contracts',
        'Customized Packages',
      ],
      brands: ['Multiple Trusted Partners'],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Our Products - NeoVam General Supplies</title>
        <meta name="description" content="Explore our comprehensive product categories: office supplies, ICT equipment, cleaning products, construction materials, hospitality supplies, and custom procurement solutions." />
      </Helmet>

      <div className="min-h-screen pt-[120px]">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-fade-in">
              Our <span className="text-gradient">Product Categories</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto animate-slide-up">
              Comprehensive supplies for every business need. From office essentials to specialized equipment, we source it all.
            </p>
          </div>
        </section>

        {/* Product Categories */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="space-y-12">
              {productCategories.map((category, index) => (
                <div
                  key={index}
                  className={`grid lg:grid-cols-2 gap-8 items-center animate-fade-in ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Icon & Info */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="card">
                      <category.icon className="w-16 h-16 text-primary-400 mb-4" />
                      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        {category.title}
                      </h2>
                      <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                        {category.description}
                      </p>
                      {category.brands && (
                        <div className="mb-6">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Featured Brands:
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {category.brands.map((brand, brandIndex) => (
                              <span
                                key={brandIndex}
                                className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-medium rounded-full"
                              >
                                {brand}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
                      >
                        Request Quote
                        <FiArrowRight />
                      </Link>
                    </div>
                  </div>

                  {/* Products List */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="card bg-gray-50 dark:bg-dark-900 border-none">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                        What We Supply:
                      </h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {category.products.map((product, productIndex) => (
                          <div
                            key={productIndex}
                            className="flex items-start gap-2"
                          >
                            <FiCheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 dark:text-gray-300">
                              {product}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-400 to-primary-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              We can source ANY product you need. No matter how specific or unique your requirements, our procurement experts are ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 text-lg font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300"
              >
                Contact Us
                <FiArrowRight />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-xl hover:bg-white hover:text-primary-600 transition-all duration-300"
              >
                Learn About Our Process
                <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Why <span className="text-gradient">Choose Us</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Quality Guaranteed',
                  description: 'Every product is inspected and verified before delivery',
                },
                {
                  title: 'Competitive Pricing',
                  description: 'We negotiate the best rates from our supplier network',
                },
                {
                  title: 'Fast Delivery',
                  description: 'Same-day delivery available in Dar es Salaam',
                },
                {
                  title: 'Flexible Payment',
                  description: 'Multiple payment options and credit facilities for businesses',
                },
                {
                  title: 'One Invoice',
                  description: 'Simplified accounting with consolidated billing',
                },
                {
                  title: 'Expert Support',
                  description: 'Dedicated account managers for personalized service',
                },
              ].map((benefit, index) => (
                <div key={index} className="card text-center hover:scale-105 animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <FiCheckCircle className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Products;
